import * as React from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { Button, Card, Spinner, Tooltip } from '@fluentui/react-components'
import { ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons'
import { useFormikContext } from 'formik'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import useLocalStorage from '../../../../hooks/useLocalStorage'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { formatTimeInMinutes } from '../../../../helpers/numberHelper'
import useUsers from '../../../../hooks/useUsers'
import { useSettings } from '../../../../hooks/useSettings'
import { ViewType } from '../../../../pages/BaseOverview/BaseOverviewFilter'
import { TimeFilterType } from '../BaseTimeOverview'
import CalendarHeader from './CalendarHeader'
import CalendarTable from './CalendarTable'
import useCalendarWindow from './useCalendarWindow'
import useDailyTotals from './useDailyTotals'
import { useTimeCalendarStyles } from './timeCalendarStyles'
import { resolveWeekNormInMinutes } from './timeCalendarHelper'

const DEFAULT_DAY_THRESHOLD_MINUTES = 240

type Props = {
    setViewType: (viewType: ViewType) => void
}

/**
 * Time writing calendar (HAM-3622). Shows the written time per day for whole weeks, newest week first, so the
 * user scrolls into the past. All overview filters apply except the date filter; clicking a day filters the
 * overview on that day and returns to the list.
 */
export default function TimeOverviewCalendar(props: Props) {
    const styles = useTimeCalendarStyles()
    const { t: translate } = useTranslation()
    const { isViewport } = useViewport()
    const { values, setValues } = useFormikContext<TimeFilterType>()
    const { getUserById } = useUsers()
    const { settings } = useSettings()
    const { host } = useContext(AuthorizationContext)

    const compact = isViewport('<=', Viewport.OutlookNew)
    // Only the workdays fit on a narrow pane; the side bar next to the table needs a wide one.
    const hideWeekend = isViewport('<=', Viewport.OutlookNew)
    const sideNavigation = isViewport('>=', Viewport.OutlookNewWide)

    const { weeks, visibleMonth, from, to, canScrollDown, scrollUp, scrollDown, jumpMonth } = useCalendarWindow()
    const { loading, totals } = useDailyTotals(values, from, to)

    // The norm belongs to a single user, so it only applies when exactly one user is selected. Without a user
    // filter, or with several users, the cells hold added up totals that no single norm can measure.
    const normUser = useMemo(() => {
        const selected = values.users ?? []
        return selected.length === 1 ? getUserById(selected[0]) : undefined
    }, [values.users, getUserById])

    const resolveNorm = useCallback((monday: DateTime) => resolveWeekNormInMinutes(normUser, monday), [normUser])
    const currentWeekNorm = useMemo(() => resolveNorm(DateTime.local().startOf('week')), [resolveNorm])
    const dayThresholdMinutes = settings?.timeCalendar?.dayThresholdMinutes ?? DEFAULT_DAY_THRESHOLD_MINUTES

    // The choice to hide the colours lives in this browser only (HAM-4315). It is deliberately never written
    // when a setting changes, so a user that hid the colours gets them back the moment the office stops
    // requiring them.
    const { values: colorsHidden, setValues: setColorsHidden } = useLocalStorage<boolean>({
        key: 'timeCalendarColorsHidden',
        hostKey: host?.RowKey,
        defaultValue: false,
        fromJson: values => values,
        toJson: values => values,
        load: host !== undefined,
    })

    // Without a norm there is nothing to measure a day against, so neither colours nor switch make sense; the
    // norm itself is only resolved for a single selected user.
    const hasNorm = !!currentWeekNorm
    // A row written before HAM-4315 carries no value, which counts as enabled.
    const colorsEnabled = settings?.timeCalendar?.colorsEnabled !== false
    const colorsRequired = settings?.timeCalendar?.colorsRequired === true
    // Listing the norm is a separate choice from colouring by it; without a norm there is no line either way.
    const showNorm = settings?.timeCalendar?.showNorm !== false
    const showColorsSwitch = hasNorm && colorsEnabled && !colorsRequired
    const colorsActive = hasNorm && colorsEnabled && (colorsRequired || !colorsHidden)

    const monthDays = useMemo(
        () => Array.from(totals.values()).filter(day => DateTime.fromISO(day.date).hasSame(visibleMonth, 'month')),
        [totals, visibleMonth],
    )

    const onSelectDate = (date: string) => {
        // The tag holds an interval filter (e.g. 'monday') that would overrule from/to on the next load.
        setValues({ ...values, tag: '', from: date, to: date })
        props.setViewType(ViewType.ListView)
    }

    return (
        <Card className={styles.card}>
            <CalendarHeader
                visibleMonth={visibleMonth}
                monthDays={monthDays}
                normInMinutes={showNorm ? currentWeekNorm : undefined}
                stacked={compact}
                showColorsSwitch={showColorsSwitch}
                colorsVisible={colorsActive}
                onToggleColors={visible => setColorsHidden(!visible)}
                onJumpMonth={jumpMonth}
            />

            {/* Too narrow for the side bar: the week navigation sits above the table instead. */}
            {!sideNavigation && (
                <div className={styles.topNav}>
                    <Tooltip withArrow relationship={'label'} content={translate('time.calendar.navigation.earlier')}>
                        <Button appearance={'subtle'} size={'small'} icon={<ChevronUpRegular />} onClick={scrollUp} />
                    </Tooltip>
                    <Tooltip withArrow relationship={'label'} content={translate('time.calendar.navigation.later')}>
                        <Button appearance={'subtle'} size={'small'} disabled={!canScrollDown} icon={<ChevronDownRegular />} onClick={scrollDown} />
                    </Tooltip>
                </div>
            )}

            <div className={styles.body}>
                {sideNavigation && (
                    <div className={styles.sideNav}>
                        <Tooltip withArrow relationship={'label'} content={translate('time.calendar.navigation.earlier')}>
                            <Button appearance={'subtle'} icon={<ChevronUpRegular />} onClick={scrollUp} />
                        </Tooltip>
                        <Tooltip withArrow relationship={'label'} content={translate('time.calendar.navigation.later')}>
                            <Button appearance={'subtle'} disabled={!canScrollDown} icon={<ChevronDownRegular />} onClick={scrollDown} />
                        </Tooltip>
                    </div>
                )}

                <CalendarTable
                    weeks={weeks}
                    totals={totals}
                    resolveNorm={resolveNorm}
                    colorsActive={colorsActive}
                    dayThresholdMinutes={dayThresholdMinutes}
                    hideWeekend={hideWeekend}
                    compact={compact}
                    onSelectDate={onSelectDate}
                />
            </div>

            {loading && <Spinner size={'tiny'} labelPosition={'after'} label={translate('base.loading')} />}

            {colorsActive && (
                <div className={styles.legend}>
                    <span className={styles.legendItem}>
                        <span className={`${styles.legendSwatch} ${styles.red}`} /> {translate('time.calendar.legend.none')}
                    </span>
                    <span className={styles.legendItem}>
                        <span className={`${styles.legendSwatch} ${styles.orange}`} />{' '}
                        {translate('time.calendar.legend.below', { value: formatTimeInMinutes(dayThresholdMinutes) })}
                    </span>
                    <span className={styles.legendItem}>
                        <span className={`${styles.legendSwatch} ${styles.green}`} />{' '}
                        {translate('time.calendar.legend.above', { value: formatTimeInMinutes(dayThresholdMinutes) })}
                    </span>
                </div>
            )}
        </Card>
    )
}
